import React, { useState, useEffect } from 'react';
import { Card, Table, Form, Select, Button, DatePicker, Space, Typography, Tag, Modal, InputNumber, message, Statistic, Row, Col, Input, Popconfirm } from 'antd';
import moment from 'moment';
import { getClients, getSoaEntries, deleteSoaEntry, recordSoaPayment, downloadSoaIndependent, sendSoaEntryEmail } from '../api';
import { DownloadOutlined, FilterOutlined, DollarOutlined, DeleteOutlined, MailOutlined } from '@ant-design/icons';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

const StatementOfAccount = () => {
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [clients, setClients] = useState([]);
    const [soaData, setSoaData] = useState([]);
    const [emailModalVisible, setEmailModalVisible] = useState(false);
    const [selectedEntry, setSelectedEntry] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [emailForm] = Form.useForm();

    const handleSearch = async (values) => {
        setLoading(true);
        try {
            const params = {
                client_name: values.client_name,
                status: values.status,
                start_date: values.monthRange?.[0]?.startOf('month').format('YYYY-MM-DD'),
                end_date: values.monthRange?.[1]?.endOf('month').format('YYYY-MM-DD'),
            };
            const res = await getSoaEntries(params);
            if (res.success) {
                setSoaData(res.data);
            }
        } catch (err) {
            message.error('Failed to fetch SOA data');
        } finally {
            setLoading(false);
        }
    };

    const handleDownload = async () => {
        const values = form.getFieldsValue();
        if (!values.client_name) {
            message.warning('Please select a client first');
            return;
        }
        try {
            message.loading({ content: 'Generating SOA PDF...', key: 'download' });
            const params = {
                client_name: values.client_name,
                status: values.status,
                start_date: values.monthRange?.[0]?.startOf('month').format('YYYY-MM-DD'),
                end_date: values.monthRange?.[1]?.endOf('month').format('YYYY-MM-DD'),
            };
            const blob = await downloadSoaIndependent(params);
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `SOA_${values.client_name === 'all' ? 'All_Clients' : values.client_name.replace(/ /g, '_')}_${moment().format('YYYYMMDD')}.pdf`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            message.success({ content: 'SOA Downloaded', key: 'download' });
        } catch (err) {
            message.error({ content: 'Failed to download SOA', key: 'download' });
        }
    };

    useEffect(() => {
        const fetchClients = async () => {
            try {
                const res = await getClients();
                if (res.success) {
                    setClients(res.data);
                }
            } catch (err) {
                console.error('Failed to fetch clients', err);
            }
        };
        fetchClients();
        handleSearch({ client_name: 'all', status: 'pending' });
    }, []);

    const handlePaymentToggle = async (id) => {
        setLoading(true);
        try {
            const res = await recordSoaPayment(id);
            if (res.success) {
                message.success(res.message || 'Payment status updated');
                handleSearch(form.getFieldsValue());
            } else {
                message.error(res.message || 'Failed to update payment status');
            }
        } catch (err) {
            message.error('Failed to update payment status');
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (id) => {
        try {
            const res = await deleteSoaEntry(id);
            if (res.success) {
                message.success('Entry deleted');
                handleSearch(form.getFieldsValue());
            }
        } catch (err) {
            message.error('Failed to delete');
        }
    };

    const showEmailModal = (record) => {
        setSelectedEntry(record);
        
        // Find client email from clients array
        const client = clients.find(c => c.name === record.client_name);
        const clientEmail = client ? client.email : '';

        const invoiceRef = record.ref_no || `Invoice ID: ${record.id}`;
        const formattedAmount = Number(record.total_amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2 });
        const formattedBalance = Number(record.balance || 0).toLocaleString(undefined, { minimumFractionDigits: 2 });
        const formattedDueDate = record.due_date ? moment(record.due_date).format('DD/MM/YYYY') : 'N/A';

        const defaultSubject = `Statement of Account Reminder - Ref: ${invoiceRef}`;
        const defaultMessage = `Dear Client,\n\nPlease find attached the details and statement for reference number: ${invoiceRef}.\n\nInvoice Summary:\n- Invoice Reference: ${invoiceRef}\n- Total Invoice Amount: AED ${formattedAmount}\n- Remaining Pending Balance: AED ${formattedBalance}\n- Due Date: ${formattedDueDate}\n\nPlease arrange the payment at your earliest convenience. If you have any queries regarding this statement, please do not hesitate to contact us.\n\nBest regards,\nDeba Technical Cont.`;

        emailForm.setFieldsValue({
            to: clientEmail,
            cc: '',
            bcc: '',
            subject: defaultSubject,
            message: defaultMessage
        });

        setEmailModalVisible(true);
    };

    const handleEmailSubmit = async (values) => {
        setSubmitting(true);
        try {
            const htmlMessage = values.message.replace(/\n/g, '<br>');
            const res = await sendSoaEntryEmail(selectedEntry.id, {
                ...values,
                message: htmlMessage
            });
            if (res.success) {
                message.success('Email sent successfully');
                setEmailModalVisible(false);
            } else {
                message.error(res.message || 'Failed to send email');
            }
        } catch (err) {
            message.error('Failed to send email');
        } finally {
            setSubmitting(false);
        }
    };

    const columns = [
        {
            title: 'Client',
            dataIndex: 'client_name',
            key: 'client_name',
            sorter: (a, b) => (a.client_name || '').localeCompare(b.client_name || ''),
        },
        {
            title: 'Invoice Date',
            dataIndex: 'date',
            key: 'date',
            render: (date) => date ? moment(date).format('DD/MM/YYYY') : '-',
            sorter: (a, b) => moment(a.date).unix() - moment(b.date).unix(),
        },
        { title: 'Ref No.', dataIndex: 'ref_no', key: 'ref_no' },
        {
            title: 'Due Date',
            dataIndex: 'due_date',
            key: 'due_date',
            render: (date) => date ? moment(date).format('DD/MM/YYYY') : '-',
            sorter: (a, b) => {
                if (!a.due_date) return -1;
                if (!b.due_date) return 1;
                return moment(a.due_date).unix() - moment(b.due_date).unix();
            },
        },
        {
            title: 'Payment Terms',
            dataIndex: 'payment_terms',
            key: 'payment_terms',
            render: (terms) => terms || '-',
            sorter: (a, b) => (a.payment_terms || '').localeCompare(b.payment_terms || ''),
        },
        {
            title: 'Amount',
            dataIndex: 'total_amount',
            key: 'total_amount',
            align: 'right',
            render: (val) => val ? val.toLocaleString(undefined, { minimumFractionDigits: 2 }) : '0.00',
        },
        {
            title: 'Paid Amount',
            dataIndex: 'paid_amount',
            key: 'paid_amount',
            align: 'right',
            render: (val) => val ? val.toLocaleString(undefined, { minimumFractionDigits: 2 }) : '0.00',
        },
        {
            title: 'Balance',
            dataIndex: 'balance',
            key: 'balance',
            align: 'right',
            render: (val) => val ? val.toLocaleString(undefined, { minimumFractionDigits: 2 }) : '0.00',
        },
        {
            title: 'Status',
            key: 'status',
            render: (_, record) => {
                if (record.balance <= 0) return <Tag color="green">PAID</Tag>;
                const isOverdue = record.due_date && moment(record.due_date).isBefore(moment(), 'day');
                if (isOverdue) return <Tag color="red">OVERDUE</Tag>;
                return <Tag color="orange">UNPAID</Tag>;
            }
        },
        {
            title: 'Actions',
            key: 'actions',
            render: (_, record) => (
                <Space>
                    <Popconfirm 
                        title={record.balance <= 0 ? "Mark this invoice as Unpaid?" : "Mark this invoice as Paid?"} 
                        onConfirm={() => handlePaymentToggle(record.id)}
                        okText="Yes" 
                        cancelText="No"
                    >
                        <Button 
                            type={record.balance <= 0 ? "default" : "primary"} 
                            size="small" 
                            icon={<DollarOutlined />}
                            title={record.balance <= 0 ? "Mark as Unpaid" : "Mark as Paid"}
                        >
                            {record.balance <= 0 ? "Mark Unpaid" : "Mark Paid"}
                        </Button>
                    </Popconfirm>
                    <Button size="small" icon={<MailOutlined />} onClick={() => showEmailModal(record)} title="Send Email Reminder" />
                    <Popconfirm title="Delete this entry?" onConfirm={() => handleDelete(record.id)} okText="Yes" cancelText="No">
                        <Button size="small" danger icon={<DeleteOutlined />} />
                    </Popconfirm>
                </Space>
            ),
        }
    ];

    const totalOpening = Array.isArray(soaData) ? soaData.reduce((sum, item) => sum + Number(item.total_amount || 0), 0) : 0;
    const totalPending = Array.isArray(soaData) ? soaData.reduce((sum, item) => sum + Number(item.balance || 0), 0) : 0;

    return (
        <div style={{ padding: '24px' }}>
            <Title level={2}>Statement of Account</Title>

            <Card style={{ marginBottom: 24 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '16px' }}>
                    <Form form={form} layout="inline" onFinish={handleSearch} initialValues={{ client_name: 'all', status: 'pending' }} style={{ flex: 1, minWidth: 0 }}>
                        <Form.Item label="Client" name="client_name" rules={[{ required: true, message: 'Select Client' }]} style={{ minWidth: 200, marginBottom: '8px' }}>
                            <Select showSearch placeholder="Select Client" options={[
                                { label: 'All Clients', value: 'all' },
                                ...(Array.isArray(clients) ? clients.map(c => ({ label: c.name, value: c.name })) : [])
                            ]} />
                        </Form.Item>
                        <Form.Item label="Status" name="status" style={{ minWidth: 150, marginBottom: '8px' }}>
                            <Select options={[
                                { label: 'All', value: 'all' },
                                { label: 'Unpaid', value: 'pending' },
                                { label: 'Paid', value: 'paid' }
                            ]} />
                        </Form.Item>
                        <Form.Item label="Month Range" name="monthRange" style={{ marginBottom: '8px' }}>
                            <RangePicker picker="month" format="MMM YYYY" />
                        </Form.Item>
                        <Form.Item style={{ marginBottom: '8px' }}><Button type="primary" icon={<FilterOutlined />} htmlType="submit" loading={loading}>Search</Button></Form.Item>
                        <Form.Item style={{ marginBottom: '8px' }}><Button icon={<DownloadOutlined />} onClick={handleDownload} disabled={!Array.isArray(soaData) || soaData.length === 0}>Download PDF</Button></Form.Item>
                    </Form>
                </div>
            </Card>

            {Array.isArray(soaData) && soaData.length > 0 && (
                <Row gutter={16} style={{ marginBottom: 24 }}>
                    <Col span={8}><Card><Statistic title="Total Billed" value={totalOpening} precision={2} suffix="AED" /></Card></Col>
                    <Col span={8}><Card><Statistic title="Total Pending" value={totalPending} precision={2} suffix="AED" valueStyle={{ color: totalPending > 0 ? '#cf1322' : '#3f8600' }} /></Card></Col>
                    <Col span={8}><Card><Statistic title="Collection %" value={totalOpening > 0 ? ((totalOpening - totalPending) / totalOpening) * 100 : 0} precision={1} suffix="%" /></Card></Col>
                </Row>
            )}

            <Card bodyStyle={{ padding: 0 }}>
                <Table columns={columns} dataSource={Array.isArray(soaData) ? soaData : []} rowKey="id" loading={loading} pagination={false}
                    summary={(pageData) => {
                        let totalBilled = 0;
                        let totalPaid = 0;
                        let totalBalance = 0;
                        if (Array.isArray(pageData)) {
                            pageData.forEach(({ total_amount, paid_amount, balance }) => {
                                totalBilled += Number(total_amount || 0);
                                totalPaid += Number(paid_amount || 0);
                                totalBalance += Number(balance || 0);
                            });
                        }
                        return (
                            <Table.Summary fixed>
                                <Table.Summary.Row>
                                    <Table.Summary.Cell index={0} colSpan={5}><b>Totals</b></Table.Summary.Cell>
                                    <Table.Summary.Cell index={1} align="right"><Text strong>{totalBilled.toLocaleString(undefined, { minimumFractionDigits: 2 })} Dr</Text></Table.Summary.Cell>
                                    <Table.Summary.Cell index={2} align="right"><Text strong>{totalPaid.toLocaleString(undefined, { minimumFractionDigits: 2 })}</Text></Table.Summary.Cell>
                                    <Table.Summary.Cell index={3} align="right"><Text strong>{totalBalance.toLocaleString(undefined, { minimumFractionDigits: 2 })}</Text></Table.Summary.Cell>
                                    <Table.Summary.Cell index={4} colSpan={2} />
                                </Table.Summary.Row>
                            </Table.Summary>
                        );
                    }}
                />
            </Card>



            <Modal title="Send Email Reminder" open={emailModalVisible} onCancel={() => setEmailModalVisible(false)} onOk={() => emailForm.submit()} confirmLoading={submitting}>
                <Form form={emailForm} layout="vertical" onFinish={handleEmailSubmit}>
                    <Form.Item label="To (Recipient Email)" name="to" rules={[{ required: true, message: 'Please enter recipient email' }]}>
                        <Input placeholder="client@example.com (use commas for multiple)" />
                    </Form.Item>
                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item label="CC" name="cc">
                                <Input placeholder="cc@example.com" />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item label="BCC" name="bcc">
                                <Input placeholder="bcc@example.com" />
                            </Form.Item>
                        </Col>
                    </Row>
                    <Form.Item label="Subject" name="subject" rules={[{ required: true, message: 'Please enter subject' }]}>
                        <Input placeholder="Email Subject" />
                    </Form.Item>
                    <Form.Item label="Message Body" name="message" rules={[{ required: true, message: 'Please enter email message' }]}>
                        <Input.TextArea rows={8} placeholder="Email Message Body" />
                    </Form.Item>
                    <p style={{ color: '#8c8c8c', fontSize: '12px' }}>
                        <i>Note: The invoice statement PDF will be generated and attached to this email automatically.</i>
                    </p>
                </Form>
            </Modal>
        </div>
    );
};

export default StatementOfAccount;
